import $, { Cash } from "cash-dom";

/**
 * Video Loader - Converts .js-video-loader divs into HTML5 video elements
 * Based on Dinamo video loader implementation
 *
 * Features:
 * - Lazy loading using Intersection Observer
 * - Responsive video sources with media queries
 * - Responsive poster images
 * - Autoplay management with viewport detection
 * - Graceful error handling for autoplay failures
 *
 * @author Stefan Rueschenberg <Stefan@Humans-Machines.com>
 * @author Raffael Stüken <Raffael@Humans-Machines.com>
 */

interface VideoSource {
    media: string;
    src: string;
}

interface PosterSource {
    width: number;
    src: string;
}

export default class VideoLoader {
    /**
     * Singleton instance
     */
    static #instance: VideoLoader;

    /**
     * UI selectors
     */
    protected ui = {
        videoContainer: ".video-player",
        videoPlayer: ".video-player__video",
        autoplayVideo: '.video-player__video[data-autoplay="true"]',
        videoLoader: ".js-video-loader",
    };

    /**
     * Intersection Observer instance
     */
    protected intersectionObserver: IntersectionObserver | null = null;

    /**
     * Protected constructor for singleton pattern
     */
    protected constructor() {
        this.init();
    }

    /**
     * Get singleton instance
     */
    public static getInstance(): VideoLoader {
        if (!VideoLoader.#instance) {
            VideoLoader.#instance = new VideoLoader();
        }
        return VideoLoader.#instance;
    }

    /**
     * Initialize video loader
     */
    protected init(): void {
        // Bind playing state for server-rendered videos
        this.bindPlayingState();

        if (this.useIntersectionObserver()) {
            this.initObserver();
            this.observeElements();
        } else {
            // Fallback: convert all loaders immediately
            this.convertAllLoaders();
        }
    }

    /**
     * Bind playing state class for server-rendered autoplay videos
     * Adds .is-playing to container when video starts playing
     */
    protected bindPlayingState(): void {
        const videos = document.querySelectorAll(`${this.ui.videoContainer} ${this.ui.videoPlayer}`);

        videos.forEach((video) => {
            const videoElement = video as HTMLVideoElement;
            const container = videoElement.closest(this.ui.videoContainer);

            if (!container) return;

            videoElement.addEventListener("playing", () => {
                container.classList.add("is-playing");
            });

            videoElement.addEventListener("pause", () => {
                container.classList.remove("is-playing");
            });
        });
    }

    /**
     * Check if Intersection Observer is supported
     */
    protected useIntersectionObserver(): boolean {
        return "IntersectionObserver" in window;
    }

    /**
     * Initialize Intersection Observer
     */
    protected initObserver(): void {
        if (this.intersectionObserver !== null) {
            return;
        }

        this.intersectionObserver = new IntersectionObserver(
            (entries) => this.handleIntersection(entries),
            {
                root: null, // Use viewport as root
                rootMargin: "25% 0px 50% 0px", // Trigger early (25% before entering viewport)
                threshold: 0, // Trigger when single pixel is visible
            }
        );
    }

    /**
     * Observe all video loader elements
     */
    protected observeElements(): void {
        if (!this.intersectionObserver) {
            return;
        }

        const loaders = document.querySelectorAll(this.ui.videoLoader);

        loaders.forEach((loader) => {
            const element = loader as HTMLElement;
            if (!element.dataset.isObserved) {
                this.intersectionObserver?.observe(element);
                element.dataset.isObserved = "true";
            }
        });
    }

    /**
     * Handle intersection observer callback
     */
    protected handleIntersection(entries: IntersectionObserverEntry[]): void {
        entries.forEach((entry) => {
            this.handleIntersectionEntry(entry);
        });
    }

    /**
     * Handle intersection for a single entry
     */
    protected handleIntersectionEntry(entry: IntersectionObserverEntry): void {
        const element = entry.target as HTMLElement;

        if (!entry.rootBounds) {
            return;
        }

        const inView = entry.isIntersecting || entry.intersectionRatio > 0;

        if (element.matches(this.ui.videoLoader)) {
            if (!inView) {
                return;
            }
            this.convertLoaderToVideo(element);
        } else if (element.matches(this.ui.videoPlayer)) {
            const autoplayValue = (element as HTMLVideoElement).dataset.autoplay;
            const hasAutoplay = autoplayValue === "true";

            if (inView && (element as HTMLVideoElement).paused && hasAutoplay) {
                this.handleAutoplay(element as HTMLVideoElement);
            } else if (!inView && !(element as HTMLVideoElement).paused && hasAutoplay) {
                (element as HTMLVideoElement).pause();
            }
        }
    }

    /**
     * Convert all loaders immediately (fallback for no IntersectionObserver)
     */
    protected convertAllLoaders(): void {
        const loaders = document.querySelectorAll(this.ui.videoLoader);
        loaders.forEach((loader) => {
            this.convertLoaderToVideo(loader as HTMLElement);
        });
    }

    /**
     * Convert a single loader div to video element
     */
    protected convertLoaderToVideo(element: HTMLElement): void {
        try {
            // Unobserve the loader
            this.intersectionObserver?.unobserve(element);

            const parent = element.parentElement;
            if (!parent) return;

            // Generate video markup
            const config = this.getVideoMarkupConfig(element);
            if (!config || !config.markup) {
                console.error("Failed to generate video markup", element);
                return;
            }

            // Replace loader with video
            element.outerHTML = config.markup;

            // Observe the new video element if autoplay is enabled
            if (this.useIntersectionObserver() && config.autoplay) {
                const videoElement = parent.querySelector(this.ui.autoplayVideo);
                if (videoElement) {
                    this.intersectionObserver?.observe(videoElement);
                }
            }
        } catch (error) {
            console.error("Error in convertLoaderToVideo:", error);
        }
    }

    /**
     * Generate video markup from loader element
     */
    protected getVideoMarkupConfig(element: HTMLElement): { markup: string; autoplay: boolean } | null {
        try {
            // Parse data attributes
            const autoplay = element.dataset.autoplay === "1";
            const muted = element.dataset.muted === "1";
            const title = element.dataset.title || "";

            // Parse sources
            const srcData = element.dataset.src;
            if (!srcData) {
                console.error("No video sources found", element);
                return null;
            }

            let sources: VideoSource[] = [];
            try {
                // Try to parse as base64 first
                const decoded = atob(srcData);
                sources = JSON.parse(decoded);
            } catch {
                // If that fails, try parsing as plain JSON
                sources = JSON.parse(srcData);
            }

            if (!sources || sources.length === 0) {
                console.error("No video sources found", element);
                return null;
            }

            let src = sources.filter(item => window.matchMedia(item.media).matches)?.shift()?.src;
            if (!src) {
                src = sources.shift()?.src;
            }

            // Get poster
            const posterSrc = this.getPosterFromLoaderElement(element);

            // Build container classes
            const containerClasses = [
                "video-player",
                autoplay ? "is-autoplay" : "",
                muted ? "is-muted" : "",
            ]
                .filter((cls) => cls.length > 0)
                .join(" ");

            // Build video attributes (without src, as we'll use source elements)
            const videoAttrs: Record<string, string | boolean> = {
                class: "video-player__video",
                "data-autoplay": autoplay ? "true" : "false",
                playsinline: true,
                disableremoteplayback: true,
            };

            if (src) {
                videoAttrs.src = src;
            }

            if (muted) {
                videoAttrs.muted = true;
            }

            if (autoplay) {
                videoAttrs.loop = true;
            }

            if (posterSrc) {
                videoAttrs.poster = posterSrc;
            }

            if (title) {
                videoAttrs.title = title;
                videoAttrs["aria-label"] = title;
            }

            // For non-observer fallback, use native autoplay
            if (!this.useIntersectionObserver() && autoplay) {
                videoAttrs.autoplay = true;
            }

            // Build video element HTML
            const attrsString = Object.entries(videoAttrs)
                .filter(([_, value]) => typeof value !== "boolean" || value)
                .map(([key, value]) => {
                    if (typeof value === "boolean") {
                        return key;
                    }
                    return `${key}="${this.escapeHtml(String(value))}"`;
                })
                .join(" ");

            // Build source elements with media queries for responsive selection
            const sourceElements = sources
                .map((source) => {
                    const srcAttr = `src="${this.escapeHtml(source.src)}"`;
                    const mediaAttr = source.media ? ` media="${this.escapeHtml(source.media)}"` : "";
                    return `<source ${srcAttr}${mediaAttr}>`;
                })
                .join("");

            const markup = `<div class="${containerClasses}"><video ${attrsString}>${sourceElements}</video></div>`;

            return {
                markup,
                autoplay,
            };
        } catch (error) {
            console.error("Error generating video markup:", error, element);
            return null;
        }
    }

    /**
     * Get the proper poster path
     * Handle multiple sizes if provided
     */
    protected getPosterFromLoaderElement(element: HTMLElement): string | null {
        const posterData = element.dataset.poster;
        if (!posterData) {
            return null;
        }

        try {
            let posters: PosterSource[] = [];

            try {
                // Try base64 decode first
                const decoded = atob(posterData);

                // Check if it's HTML markup (picture element)
                if (decoded.includes("<picture") || decoded.includes("<img")) {
                    // Extract first img src from HTML
                    const match = decoded.match(/src=["']([^"']+)["']/);
                    return match ? match[1] : null;
                }

                // Otherwise parse as JSON
                posters = JSON.parse(decoded);
            } catch {
                // Try plain JSON
                posters = JSON.parse(posterData);
            }

            if (!posters || posters.length === 0) {
                return null;
            }

            // Get element width
            const elementWidth = element.offsetWidth || window.innerWidth;

            // Find smallest poster that's larger than element width
            const candidates = posters.filter((item) => item.width >= elementWidth);

            if (candidates.length > 0) {
                // Sort by width ascending and return first
                candidates.sort((a, b) => a.width - b.width);
                return candidates[0].src;
            }

            // If no match, return largest poster
            posters.sort((a, b) => b.width - a.width);
            return posters[0]?.src || null;
        } catch (error) {
            console.error("Error selecting poster source:", error);
            return null;
        }
    }

    /**
     * Handle autoplay when video enters viewport
     */
    protected handleAutoplay(videoElement: HTMLVideoElement): void {
        const playPromise = videoElement.play();

        if (playPromise !== undefined) {
            playPromise.catch((error) => {
                if (error.name === "AbortError") {
                    // Retry once
                    videoElement.play().catch((retryError) => {
                        if (retryError.name === "NotAllowedError") {
                            this.onAutoplayNotAllowedError(videoElement);
                        }
                    });
                } else if (error.name === "NotAllowedError") {
                    this.onAutoplayNotAllowedError(videoElement);
                }
            });
        }
    }

    /**
     * Handle autoplay not allowed error (battery save mode, user settings, etc.)
     */
    protected onAutoplayNotAllowedError(videoElement: HTMLVideoElement): void {
        const container = videoElement.closest(this.ui.videoContainer);
        if (!container) return;

        // Remove autoplay classes and attributes
        container.classList.remove("is-autoplay");
        container.classList.add("is-autoplay-not-allowed");
        videoElement.removeAttribute("autoplay");
        videoElement.dataset.autoplay = "false";

        // Unobserve the video
        this.intersectionObserver?.unobserve(videoElement);
    }

    /**
     * Escape HTML for attributes
     */
    protected escapeHtml(text: string): string {
        const map: Record<string, string> = {
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
            "'": "&#039;",
        };
        return text.replace(/[&<>"']/g, (m) => map[m]);
    }

    /**
     * Public method to reinitialize video loaders in a container
     * Useful for dynamically loaded content
     */
    public reinitialize($container?: Cash): void {
        const container = $container?.[0] || document;
        const loaders = container.querySelectorAll(this.ui.videoLoader);

        if (this.useIntersectionObserver()) {
            loaders.forEach((loader) => {
                const element = loader as HTMLElement;
                if (!element.dataset.isObserved) {
                    this.intersectionObserver?.observe(loader);
                    element.dataset.isObserved = "true";
                }
            });
        } else {
            loaders.forEach((loader) => {
                this.convertLoaderToVideo(loader as HTMLElement);
            });
        }
    }
}